import { gql } from "@apollo/client";

// Send OTP mutation
export const SEND_OTP_MUTATION = gql`
	mutation SendOtp($params: AuthParams) {
		auth(params: $params) {
			sendOtp
		}
	}
`;

// Send OTP mutation
export const SEND_EMAIL_OTP_MUTATION = gql`
	mutation Auth($params: AuthParams) {
		auth(params: $params) {
			sendOtpMail
		}
	}
`;

// Login mutation (verify OTP and get user data)
export const LOGIN_MUTATION = gql`
	mutation Login($params: AuthParams) {
		auth(params: $params) {
			login {
				tokenData {
					token
					expiresAt
				}
				user {
					id
					name
					relation
					groups {
						relationship
						members {
							name
							id
							designation
						}
						projects {
							noOfFlatsForSale
							project {
								id
								name
							}
							totalFlats
						}
						type
						name
						id
					}
					phones {
						number
						verified
					}
					emails {
						address
						verified
					}
					profile {
						image
						name
						addresses {
							type
							value
						}
						idProofs {
							type
							id
						}
						utcOffset {
							offset
							utc
							value
						}
					}
					internal
				}
			}
		}
	}
`;

// Verify OTP mutation (for phone verification without login)
export const VERIFY_OTP_MUTATION = gql`
	mutation VerifyOtp($params: AuthParams) {
		auth(params: $params) {
			verifyOtp
		}
	}
`;

// Verify OTP mutation (for email verification without login)
export const VERIFY_EMAIL_OTP_MUTATION = gql`
	mutation Mutation($params: AuthParams) {
		auth(params: $params) {
			verifyOtpMail
		}
	}
`;

// Logout mutation
const LOGOUT_MUTATION = gql`
	mutation Logout($params: AuthParams) {
		auth(params: $params) {
			logout
		}
	}
`;

// Expire login tokens mutation
const EXPIRE_LOGIN_TOKENS_MUTATION = gql`
	mutation ExpireLoginTokens($params: AuthParams) {
		auth(params: $params) {
			expireLoginTokens
		}
	}
`;

// Get user data query
export const GET_USER_QUERY = gql`
	query GetUser($params: AuthQueryParams) {
		auth(params: $params) {
			user {
				id
				name
				relation
				groups {
					relationship
					members {
						name
						id
						designation
					}
					type
					name
					id
				}
				phones {
					number
					verified
				}
				emails {
					address
					verified
				}
				profile {
					image
					name
					addresses {
						type
						value
					}
					idProofs {
						type
						id
					}
					utcOffset {
						offset
						utc
						value
					}
				}
			}
		}
	}
`;

// TypeScript interfaces for the auth parameters and responses
interface SendOtpParams {
	phone: string;
	resend?: boolean;
	sendHashCodes?: boolean;
	purpose?: string;
	whatsapp?: boolean;
}

interface SendEmailOtpParams {
	email: string;
	name?: string;
	resend?: boolean;
}

interface LoginParams {
	phone: string;
	otp: string;
}

interface VerifyOtpParams {
	phone: string;
	otp: string;
	purpose?: string;
	expectedUserId?: string;
	url?: string;
}

interface VerifyEmailOtpParams {
	email: string;
	otp: string;
	purpose?: string;
	expectedUserId?: string;
	url?: string;
}

interface AddOneSignalSubscriptionParams {
	oneSignalPlayerId: string;
	oneSignalAppId: string;
}

interface OneSignalInputParams {
	addSubscriptionId: AddOneSignalSubscriptionParams;
}

export interface AuthParams {
	sendOtp?: SendOtpParams;
	sendOtpMail?: SendEmailOtpParams;
	login?: LoginParams;
	verifyOtp?: VerifyOtpParams;
	verifyOtpMail?: VerifyEmailOtpParams;
	logout?: Record<string, never>;
	expireLoginTokens?: {
		userId?: string;
		before?: string;
		ignoreTokens?: string[];
	};
	addOneSignalSubscription?: AddOneSignalSubscriptionParams;
}

interface TokenData {
	token: string;
	expiresAt: string;
}

interface UserProfile {
	image?: string;
	name?: string;
	addresses?: Array<{
		type: string;
		value: string;
	}>;
	idProofs?: Array<{
		type: string;
		id: string;
	}>;
	utcOffset?: {
		offset: number;
		utc: string;
		value: string;
	};
}

interface Phone {
	number: string;
	verified: boolean;
}

interface Email {
	address: string;
	verified: boolean;
}

interface GroupMember {
	name: string;
	id: string;
	designation?: string;
}
interface GroupRawProject {
	noOfFlatsForSale: number;
	totalFlats: number;
	project: {
		id: string;
		name: string;
	};
	propertyTypes?: string[];
}

interface UserGroup {
	relationship: string;
	members: GroupMember[];
	type: string;
	name: string;
	id: string;
	projects?: GroupRawProject[];
}

export interface User {
	id: string;
	name: string;
	relation?: string;
	groups: UserGroup[];
	phones: Phone[];
	emails: Email[];
	profile: UserProfile;
	internal?: boolean;
}

export interface LoginResponse {
	tokenData: TokenData;
	user: User;
}

export interface SendOtpResponse {
	sendOtp: boolean;
	sendOtpMail: boolean;
}

export interface VerifyOtpResponse {
	verifyOtp: boolean;
	verifyOtpMail: boolean;
}

interface LogoutResponse {
	logout: boolean;
}

interface ExpireTokensResponse {
	expireLoginTokens: boolean;
}

interface AddOneSignalSubscriptionResponse {
	addSubscriptionId: boolean;
}

interface GetUserResponse {
	user: User;
}
